
import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { doc, onSnapshot, setDoc, getDoc, updateDoc, increment, collection, query } from 'firebase/firestore';

export interface UserAssets {
    krw: number;
    btc: number;
    eth: number;
    usdt: number;
    sol: number;
    trx: number;
    bera: number;
    btcCostBasis?: number;
    ethCostBasis?: number;
    usdtCostBasis?: number;
    trxCostBasis?: number;
    beraCostBasis?: number;
    kycLevel: number;
    name: string;
    totalEtfValue?: number;
    dailyStartValue?: number;
    lastUpdatedDate?: string;
}

const DEFAULT_ASSETS: UserAssets = {
    krw: 0,
    btc: 0,
    eth: 0,
    usdt: 0,
    sol: 0,
    trx: 0,
    bera: 0,
    btcCostBasis: 0,
    ethCostBasis: 0,
    usdtCostBasis: 0,
    trxCostBasis: 0,
    beraCostBasis: 0,
    kycLevel: 1,
    name: ''
};

export const useUserAssets = (userId?: string) => {
    const [assets, setAssets] = useState<UserAssets | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        let baseData: UserAssets = { ...DEFAULT_ASSETS };
        let etfTotalEval = 0;

        const userDocRef = doc(db, 'users', userId);
        const etfPosRef = collection(db, 'users', userId, 'etf_positions');

        const updateCombinedAssets = () => {
            setAssets({ ...baseData, totalEtfValue: etfTotalEval });
            setLoading(false);
        };

        // 1. 기본 자산 정보 (KRW, BTC 등) 구독
        const unsubscribeAssets = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
                baseData = { ...DEFAULT_ASSETS, ...docSnap.data() } as UserAssets;
            } else {
                // 문서가 없으면 기본값(0)만 사용, 자동 생성하지 않음
                baseData = { ...DEFAULT_ASSETS };
            }
            updateCombinedAssets();
        });

        // 2. ETF 포지션 정보 구독 및 평가액 계산
        const unsubscribeEtf = onSnapshot(etfPosRef, async (etfSnap) => {
            let currentEval = 0;
            for (const posDoc of etfSnap.docs) {
                const pos = posDoc.data();
                const etfDoc = await getDoc(doc(db, 'etf_products', posDoc.id));
                if (etfDoc.exists()) {
                    const etfData = etfDoc.data();
                    currentEval += (pos.quantity * (etfData.currentPrice || etfData.basePrice));
                }
            }
            etfTotalEval = currentEval;
            updateCombinedAssets();
        });

        return () => {
            unsubscribeAssets();
            unsubscribeEtf();
        };
    }, [userId]);

    const updateBalance = async (asset: keyof UserAssets, amount: number) => {
        if (!userId) throw new Error("User not authenticated");
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, {
            [asset]: increment(amount)
        });
    };

    const checkAndUpdateDailyStart = async (currentTotalValue: number) => {
        if (!userId || !assets) return;

        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const lastDate = assets.lastUpdatedDate;

        // 날짜가 바뀌었거나 처음 설정하는 경우
        if (!lastDate || lastDate !== today) {
            const userDocRef = doc(db, 'users', userId);
            await updateDoc(userDocRef, {
                dailyStartValue: currentTotalValue,
                lastUpdatedDate: today
            });
        }
    };

    return { assets, loading, updateBalance, checkAndUpdateDailyStart };
};
